import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges Tailwind classes and resolves conflicts — the later class wins.
 *
 *   cn('px-3 py-2', isLarge && 'px-6')  →  'py-2 px-6'
 *
 * Without this, a `className` prop passed into a component cannot override the
 * component's own padding, because both classes end up in the stylesheet.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Initials from a name the API sends whole — "Aarav Kumar Sharma" → "AS".
 *
 * The server builds a child's name in one piece, so screens that show an avatar
 * for one cannot use `getInitials`. A single middle name is skipped rather than
 * squeezed in: two letters is what the circle holds.
 */
export function getNameInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';

  return getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : parts[0]);
}

/**
 * A Google Maps search link for a postal address.
 *
 * A search, not a pin: institutes are onboarded with an address and no
 * coordinates, and Maps resolves an Indian street address well enough to land
 * on the campus. Blank parts are dropped so an address without a locality does
 * not search for ", , Nashik".
 */
export function googleMapsSearchUrl(parts: Array<string | null | undefined>): string {
  const query = parts.filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * A `tel:` link for a number as people type it — "+91 98765 43210",
 * "98765-43210". Everything but digits and a leading plus is dropped, which is
 * all a dialler accepts.
 */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
